/**
 * "New message" (Slack's ⌘N / the compose button): a To: field taking one or more people (chips, best matches as you
 * type, at most MAX_DM_PEOPLE with me), then a message. The conversation with exactly those people opens if it exists
 * (`channelId`); otherwise it is created on the server when the first message goes out (or on "Open" with no
 * message). With nobody picked it lists my DMs and group DMs (Browse DMs, the sidebar's "More conversations…").
 * "Add people" on a DM opens this prefilled with the conversation's people (Slack starts a new group DM).
 */
import type { SendInput } from "../logic/outbox.ts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import type { MemberOption } from "../logic/channels.ts";
import { browseDms, conversationWith, dmCandidates, firstName, recipientsLeft, type DmListRow } from "../logic/directMessages.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

const SUGGESTIONS = 8;

export type NewMessageState = {
  /** What is being typed in the To: field. */
  query: string;
  /** Picked people, in the order picked. */
  recipients: MemberOption[];
  /** People matching `query` not picked yet (empty once the limit is reached). */
  suggestions: MemberOption[];
  /** Highlighted suggestion (↑ ↓, Enter picks it). */
  active: number;
  /** The To: field is full (MAX_DM_PEOPLE with me). */
  atLimit: boolean;
  /** Nobody picked: my conversations matching `query` (Browse DMs). */
  conversations: DmListRow[];
  /** The existing conversation with exactly `recipients`. */
  channelId?: string;
  /** What the conversation is called ("Bo, Camille"). */
  title: string;
  draft: string;
  /** A message can go out (people picked and text typed; creating a conversation needs the network). */
  canSend: boolean;
  /** "Open" (go to the conversation without a message) is offered. */
  canOpen: boolean;
  busy: boolean;
  error?: MessageDescriptor;
};

function initial(): NewMessageState {
  return { query: "", recipients: [], suggestions: [], active: 0, atLimit: false, conversations: [], title: "", draft: "", canSend: false, canOpen: false, busy: false };
}

export class NewMessageViewModel extends ViewModel<NewMessageState> {
  private readonly ws: WorkspaceViewModel;

  constructor(ws: WorkspaceViewModel) {
    super(initial());
    this.ws = ws;
    this.onDispose(ws.subscribe(() => this.derive({})));
    this.derive({});
  }

  private derive(patch: Partial<NewMessageState>): void {
    const s: NewMessageState = { ...this.state, ...patch };
    const cache = this.ws.state.cache;
    if (cache === undefined) {
      this.setState(s);
      return;
    }
    const now = this.ws.deps.clock.now();
    const picked = s.recipients.map((r) => r.userId);
    const next: NewMessageState = { ...s };
    next.atLimit = recipientsLeft(picked.length) === 0;
    next.suggestions = s.query.trim() === "" && picked.length === 0 ? [] : dmCandidates(cache, s.query, picked, now, SUGGESTIONS);
    next.active = Math.min(s.active, Math.max(0, next.suggestions.length - 1));
    next.conversations = picked.length === 0 ? browseDms(cache, s.query) : [];
    const existing = picked.length === 0 ? undefined : conversationWith(cache, picked);
    if (existing === undefined) delete next.channelId;
    else next.channelId = existing.id;
    next.title = picked.length === 0 ? "" : picked.length === 1 ? s.recipients[0]!.name : s.recipients.map((r) => firstName(r.name)).join(", ");
    const online = this.ws.state.network;
    next.canOpen = picked.length > 0 && !s.busy && (existing !== undefined || online);
    next.canSend = next.canOpen && s.draft.trim() !== "";
    this.setState(next);
  }

  /** Empty form, or prefilled with these people ("Add people" on a DM, "Message" on several profiles). */
  reset(userIds: readonly string[] = []): void {
    const cache = this.ws.state.cache;
    const recipients: MemberOption[] = [];
    for (const id of userIds) {
      const m = cache?.members[id];
      if (m === undefined || id === cache?.meId || recipients.some((r) => r.userId === id)) continue;
      const o: MemberOption = { userId: id, name: m.displayName, isAgent: m.kind === "agent" };
      if (m.avatarUrl !== undefined) o.avatarUrl = m.avatarUrl;
      recipients.push(o);
    }
    this.setState(initial());
    this.derive({ recipients: recipients.slice(0, recipientsLeft(0)) });
  }

  setQuery(query: string): void {
    this.derive({ query, active: 0, error: undefined });
  }

  setDraft(draft: string): void {
    this.derive({ draft, error: undefined });
  }

  add(userId: string): void {
    const p = this.state.suggestions.find((x) => x.userId === userId);
    if (p === undefined || this.state.atLimit) return;
    this.derive({ recipients: [...this.state.recipients, p], query: "", active: 0, error: undefined });
  }

  remove(userId: string): void {
    this.derive({ recipients: this.state.recipients.filter((r) => r.userId !== userId), error: undefined });
  }

  /** Backspace in an empty To: field removes the last chip. */
  removeLast(): void {
    if (this.state.query !== "" || this.state.recipients.length === 0) return;
    this.derive({ recipients: this.state.recipients.slice(0, -1) });
  }

  /** ↑ (-1) / ↓ (+1) through the suggestions. */
  move(delta: number): void {
    const n = this.state.suggestions.length;
    if (n === 0) return;
    this.derive({ active: (this.state.active + delta + n) % n });
  }

  /** Enter in the To: field: picks the highlighted suggestion. */
  pickActive(): boolean {
    const p = this.state.suggestions[this.state.active];
    if (p === undefined) return false;
    this.add(p.userId);
    return true;
  }

  /** The conversation with the picked people, created if needed. Resolves with its id, or undefined (`error`). */
  async open(): Promise<string | undefined> {
    const s = this.state;
    if (!s.canOpen) return undefined;
    if (s.channelId !== undefined) return s.channelId;
    this.derive({ busy: true, error: undefined });
    try {
      const c = await this.ws.deps.data.createChannel(this.ws.state.wsId, { kind: s.recipients.length > 1 ? "group_dm" : "dm", name: "", memberIds: s.recipients.map((r) => r.userId) });
      if (!this.isDisposed) this.derive({ busy: false });
      return c.id;
    } catch {
      if (!this.isDisposed) this.derive({ busy: false, error: msg("newMessage.failed") });
      return undefined;
    }
  }

  /** Sends the draft to the conversation (creating it first if needed). Resolves with the conversation's id. */
  async send(): Promise<string | undefined> {
    const text = this.state.draft.trim();
    if (!this.state.canSend) return undefined;
    const channelId = await this.open();
    if (channelId === undefined) return undefined;
    const input: SendInput = { clientId: this.ws.deps.ids.next(), channelId, text, createdAt: this.ws.deps.clock.now() };
    this.ws.deps.data.send(this.ws.state.wsId, input);
    if (!this.isDisposed) this.derive({ draft: "" });
    return channelId;
  }
}

